import { useMutation, useQuery } from '@tanstack/react-query'
import AXIOS_INSTANCE from '../api/axiosInstance'
import { queryClient } from '../store/provider/TanStackProvider'
import { FamilyDataMock, HistoryResMock } from '../types/history.type'
import { statusType } from '../types/medication.type'

export type FamilyProps = {
  patientId?: string
  stat?: boolean
  date?: string
  id?: string
  limit?: number | string
  query?: string
  page?: number | string
  encounterId?: string
  sortDir?: 'asc' | 'desc'
  status?: (keyof typeof statusType)[]
  patient?: string
  prescribed?: boolean
  reconciliation?: boolean
  sortBy?: string
  intakeId?: string
  encounter_type?: string
  context?: 'intake' | 'admitted' | 'post_discharge' | 'outpatient' | 'outpatient_administered'
}

export const useFamily = (arg?: FamilyProps) => {
  const patientId = arg?.patientId
  const id = arg?.id
  const intakeId = arg?.intakeId
  const sortDir = arg?.sortDir || 'desc'

  const { data: families, isLoading } = useQuery({
    queryFn: async () => {
      let endpoint = `family_histories?`
      if (intakeId) endpoint += `intake_id=${intakeId}&`
      if (patientId) endpoint += `patient_id=${patientId}&`
      if (sortDir) endpoint += `sort_dir=${sortDir}&`
      const { data } = await AXIOS_INSTANCE.get<HistoryResMock<FamilyDataMock[]>>(endpoint)
      return data.data
    },
    queryKey: ['family', { id, intakeId, patientId }]
  })

  const { mutateAsync: createFamily } = useMutation({
    mutationFn: async (formVal: any) => {
      const { data } = await AXIOS_INSTANCE.post(`family_histories`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['family', { id }] })
  })

  const { mutateAsync: updateFamily } = useMutation({
    mutationFn: async ({ formVal, id }: { formVal: any; id: string }) => {
      const { data } = await AXIOS_INSTANCE.patch(`family_histories/${id}`, formVal, {
        headers: { 'Content-Type': 'application/vnd.api+json' }
      })
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['family', { id }] })
  })

  const { mutateAsync: deleteFamily } = useMutation({
    mutationFn: async ({ id }: { id: string }) => {
      await AXIOS_INSTANCE.delete(`family_histories/${id}`)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['family'] })
  })

  return { createFamily, deleteFamily, families, isLoading, updateFamily }
}
